import { useMsal } from "@azure/msal-react";
import {
	assertSuccessStatusCode,
	AuthenticateApi,
	BankIdApi,
	BannersApi,
	CallCenterApi,
	ChangeRequestsApi,
	Configuration,
	type ConfigurationParameters,
	ConsentApi,
	ContactsApi,
	ContractsApi,
	ContractsDpsApi,
	ContractsLifeApi,
	ContractsPpApi,
	DocumentsApi,
	getHeaders,
	type IContextPrimaryAPIClient,
	InvestorProfileApi,
	PostLoginApi,
	ProfileApi,
	RegistrationApi,
} from "@repo/api";
import { EnvironmentHelper, readEnv } from "@repo/helpers";
import { sagaMiddlewareTask } from "@repo/logic";
import { createContext, type FC, type PropsWithChildren, useContext } from "react";
import { useDataConfig } from "./DataConfigContext";

// Load environment variables at module level (like in gatsby-shared.tsx)
const GATSBY_API_BASE_URL = readEnv("GATSBY_API_BASE_URL");
const GATSBY_B2C_API_SCOPE = readEnv("GATSBY_B2C_API_SCOPE");

export const PrimaryAPIClientContext = createContext<IContextPrimaryAPIClient>({} as IContextPrimaryAPIClient);

export function usePrimaryAPIClient(): IContextPrimaryAPIClient {
	return useContext(PrimaryAPIClientContext);
}

export const PrimaryAPIClientProvider: FC<PropsWithChildren> = ({ children }) => {
	const { config } = useDataConfig();
	const { instance } = useMsal();

	const configurationParameters: ConfigurationParameters = {
		async accessToken() {
			// TODO odchytit kdyz se nepovede acquirenout https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/acquire-token.md#acquiring-an-access-token
			// TODO az se objevi druhe volani, udelame abstraktni metodu
			const tokenResponse =
				EnvironmentHelper.isLocal() && config.overrideToken
					? config.overrideToken
					: (
						await instance.acquireTokenSilent({
							scopes: ["openid", GATSBY_B2C_API_SCOPE || ""],
						})
					).accessToken;
			return `Bearer ${tokenResponse}`;
		},
		headers: getHeaders(!EnvironmentHelper.isProd() ? config : undefined),
		basePath: GATSBY_API_BASE_URL,
		middleware: [
			{
				post: async (context) => {
					await assertSuccessStatusCode(context.response);
				},
			},
		],
	};

	const configuration = new Configuration(configurationParameters);

	const apiClient = {
		callCenterApi: new CallCenterApi(configuration),
		profileApi: new ProfileApi(configuration),
		changeRequestsApi: new ChangeRequestsApi(configuration),
		consentApi: new ConsentApi(configuration),
		contactsApi: new ContactsApi(configuration),
		contractsApi: new ContractsApi(configuration),
		documentsApi: new DocumentsApi(configuration),
		investorProfileApi: new InvestorProfileApi(configuration),
		contractsDpsApi: new ContractsDpsApi(configuration),
		contractsLifeApi: new ContractsLifeApi(configuration),
		contractsPpApi: new ContractsPpApi(configuration),
		authenticateApi: new AuthenticateApi(configuration),
		postLoginApi: new PostLoginApi(configuration),
		registrationApi: new RegistrationApi(configuration),
		bannersApi: new BannersApi(configuration),
		bankIdApi: new BankIdApi(configuration),
		configurationParameters,
	}

	sagaMiddlewareTask.setContext({ apiClient });

	return (
		<PrimaryAPIClientContext.Provider
			value={apiClient}
		>
			{children}
		</PrimaryAPIClientContext.Provider>
	);
}
